import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { mobile, tablet } from "../responsive";

const Container = styled.div`
    position: relative;
    width: 100%;
    height: 100vh;
    background: var(--brand-black);
    overflow: hidden;
    ${mobile({ display: "none" })}
    ${tablet({ height: "100%" })}

    &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 4rem;
        background: linear-gradient(to top, var(--brand-black), transparent);
        pointer-events: none;
        z-index: 1;
    }
`;

const VideoBg = styled.video`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    ${mobile({ display: "none" })}
`;

const VIDEO_SRC =
    "https://ik.imagekit.io/dnddecpho/dndream/D&D_CarConvoy2024_d3BvBxmYj.mp4?updatedAt=1739982689534";

const isMobileDevice = () =>
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    );

const tryPlay = (video) => {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === "function") {
        return playPromise.then(() => true).catch(() => false);
    }
    return Promise.resolve(true);
};

const Video = ({ url = VIDEO_SRC }) => {
    const videoRef = useRef(null);
    const [isMobile] = useState(isMobileDevice);

    useEffect(() => {
        if (isMobile) return;

        const video = videoRef.current;
        if (!video) return;

        const gestureEvents = ["pointerdown", "keydown", "touchstart"];

        const forceUnmuted = () => {
            video.muted = false;
            video.defaultMuted = false;
            video.removeAttribute("muted");
            video.volume = 1;
        };

        const removeGestureListeners = () => {
            gestureEvents.forEach((eventName) => {
                window.removeEventListener(eventName, unmuteFromGesture);
            });
        };

        const unmuteFromGesture = async () => {
            forceUnmuted();
            const started = await tryPlay(video);
            if (started) removeGestureListeners();
        };

        const listenForUnmute = () => {
            gestureEvents.forEach((eventName) => {
                window.addEventListener(eventName, unmuteFromGesture, {
                    once: true,
                    passive: true,
                });
            });
        };

        const startPlayback = async () => {
            // Always aim for sound on load.
            forceUnmuted();
            let started = await tryPlay(video);

            if (started && !video.paused && !video.muted) {
                return;
            }

            // If the browser blocks sound, keep the video playing muted
            // so it still starts by default, then unmute on first gesture.
            video.muted = true;
            started = await tryPlay(video);
            if (started) {
                // One more immediate unmute attempt (works for some returning visits).
                forceUnmuted();
                const withSound = await tryPlay(video);
                if (!withSound || video.muted) {
                    video.muted = true;
                    await tryPlay(video);
                    listenForUnmute();
                }
            } else {
                listenForUnmute();
            }
        };

        if (video.readyState >= 2) {
            startPlayback();
        } else {
            video.addEventListener("canplay", startPlayback, { once: true });
            video.load();
        }

        return () => {
            video.removeEventListener("canplay", startPlayback);
            removeGestureListeners();
        };
    }, [isMobile, url]);

    if (isMobile) {
        return null;
    }

    return (
        <Container>
            <VideoBg
                ref={videoRef}
                autoPlay
                loop
                playsInline
                preload="auto"
                controls={false}
            >
                <source src={url} type="video/mp4" />
            </VideoBg>
        </Container>
    );
};

export default Video;
